import { IReportLayout, IReportOptions, ReportSection, ReportType } from './report.types'

interface SectionInfo {
  title: string
  /** The section's opening sentence: fixed template wording, not written by AL. */
  lead: string
}

/** Every section a report can carry, in the order they print. */
export const REPORT_SECTIONS: Record<ReportSection, SectionInfo> = {
  [ReportSection.EXECUTIVE_SUMMARY]: {
    title: 'Executive Summary',
    lead: 'This section summarises the overall findings of the comparison.',
  },
  [ReportSection.POLICIES]: {
    title: 'Policies Compared',
    lead: "The following policies were compared, as described in each insurer's policy wording.",
  },
  [ReportSection.CLIENT_CONTEXT]: {
    title: 'Client Context',
    lead:
      'The client details below were supplied to tailor this comparison. They are used to ' +
      'quantify and contextualise the differences identified, and do not alter what any ' +
      'policy provides.',
  },
  [ReportSection.SIMILARITIES]: {
    title: 'Areas of Similarity',
    lead: 'The policies provide comparable cover in the following areas.',
  },
  [ReportSection.DIFFERENCES]: {
    title: 'Key Differences',
    lead: 'The following material differences were identified between the policy wordings.',
  },
  [ReportSection.RISKS]: {
    title: 'Risk Assessment and Board Considerations',
    lead: 'The differences identified are considered below against key exposures.',
  },
  [ReportSection.RECOMMENDATION]: {
    title: 'Recommendation',
    lead: 'Based on the comparison, the following is put forward for consideration.',
  },
}

/** The small label above the document title. */
export const REPORT_LABELS: Record<ReportType, string> = {
  [ReportType.SUMMARY]: 'Executive Summary Report',
  [ReportType.BOARD]: 'Board Paper',
  [ReportType.TAILORED]: 'Tailored Comparison Report',
  [ReportType.KEY_DIFFERENCES]: 'Key Differences Report',
}

interface ReportTypeInfo {
  label: string
  description: string
  /** The number of policies the type compares, where it takes only that many. */
  policies?: number
  /** A note on the type's card, e.g. that it is still being finished. */
  note?: string
}

export const REPORT_TYPES: Record<ReportType, ReportTypeInfo> = {
  [ReportType.SUMMARY]: {
    label: 'Summary',
    description: 'One-page overview with a recommendation.',
  },
  [ReportType.BOARD]: {
    label: 'Board paper',
    description: 'Full paper for a board pack.',
  },
  [ReportType.TAILORED]: {
    label: 'Tailored',
    description: "Applies your client's details.",
    note: 'Coming soon',
  },
  [ReportType.KEY_DIFFERENCES]: {
    label: 'Key differences',
    description: 'What differs between two policies, area by area.',
    policies: 2,
  },
}

/**
 * Why this report type cannot be built from this many policies, or null when it can. Key
 * differences sets one policy against the other, so it takes exactly two.
 */
export function reportTypeProblem(type: ReportType, count: number): string | null {
  const { label, policies } = REPORT_TYPES[type]
  if (policies === undefined || count === policies) return null
  return `${label} compares exactly ${policies} policies. Choose another report type, or compare ${policies}.`
}

export const DEFAULT_REPORT_OPTIONS: IReportOptions = { type: ReportType.SUMMARY }

/** How many policies a report compares: two at least, and no more than its tables and the
 * endpoint's write-up hold well on a page. */
export const MIN_REPORT_POLICIES = 2
export const MAX_REPORT_POLICIES = 4

/** Why a report cannot be built from this many policies, or null when it can. */
export function policyCountProblem(count: number): string | null {
  if (count < MIN_REPORT_POLICIES)
    return `Select at least ${MIN_REPORT_POLICIES} policies to build a report`
  if (count > MAX_REPORT_POLICIES) {
    const extra = count - MAX_REPORT_POLICIES
    return `Reports compare up to ${MAX_REPORT_POLICIES} policies. Remove ${extra} to build one`
  }
  return null
}

const LAYOUTS: Record<ReportType, Omit<IReportLayout, 'type'>> = {
  [ReportType.SUMMARY]: {
    sections: [
      ReportSection.EXECUTIVE_SUMMARY,
      ReportSection.SIMILARITIES,
      ReportSection.DIFFERENCES,
      ReportSection.RECOMMENDATION,
    ],
    detail: 'brief',
    cover: false,
    contents: false,
    newPagePerSection: false,
  },
  [ReportType.BOARD]: {
    sections: [
      ReportSection.EXECUTIVE_SUMMARY,
      ReportSection.POLICIES,
      ReportSection.SIMILARITIES,
      ReportSection.DIFFERENCES,
      ReportSection.RISKS,
      ReportSection.RECOMMENDATION,
    ],
    detail: 'full',
    cover: true,
    contents: true,
    newPagePerSection: true,
  },
  // The standard comparison with the client's context applied — no recommendation, as a
  // tailored comparison explains what differences mean rather than advising on them.
  [ReportType.TAILORED]: {
    sections: [
      ReportSection.EXECUTIVE_SUMMARY,
      ReportSection.POLICIES,
      ReportSection.CLIENT_CONTEXT,
      ReportSection.DIFFERENCES,
    ],
    detail: 'full',
    cover: true,
    contents: true,
    newPagePerSection: false,
  },
  // One policy set against the other: what they are, then where they differ. Short enough
  // to need no cover or contents.
  [ReportType.KEY_DIFFERENCES]: {
    sections: [ReportSection.POLICIES, ReportSection.DIFFERENCES],
    detail: 'full',
    cover: false,
    contents: false,
    newPagePerSection: false,
  },
}

export function resolveLayout(options: IReportOptions): IReportLayout {
  return { type: options.type, ...LAYOUTS[options.type] }
}

export const REPORT_TITLES: Record<ReportType, string> = {
  [ReportType.SUMMARY]: 'Executive Summary',
  [ReportType.BOARD]: 'Board Insurance Review',
  [ReportType.TAILORED]: 'Tailored Policy Comparison',
  [ReportType.KEY_DIFFERENCES]: 'Key Differences',
}

export const REPORT_FILE_NAMES: Record<ReportType, string> = {
  [ReportType.SUMMARY]: 'LMI-Executive-Summary.pdf',
  [ReportType.BOARD]: 'LMI-Board-Paper.pdf',
  [ReportType.TAILORED]: 'LMI-Tailored-Comparison.pdf',
  [ReportType.KEY_DIFFERENCES]: 'LMI-Key-Differences.pdf',
}
